// Fetch functions, query keys and hooks of People and organisation
// (/api/workforce/settings, /api/org-unit-kinds, /api/org-units,
// /api/employees, /api/salary-scales). Types from @convenor/shared/v3.
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import type {
  Employee,
  EmployeeInput,
  EmployeePage,
  EmployeePatchInput,
  EmployeesQueryInput,
  OrgUnit,
  OrgUnitInput,
  OrgUnitKind,
  OrgUnitKindsInput,
  OrgLocale,
  OrgUnitPatchInput,
  SalaryScale,
  SalaryScalesInput,
  UnitOverview,
  WorkforceSettings,
  WorkforceSettingsInput,
} from "@convenor/shared/v3";
import { apiV3 } from "../../api/v3";

// ---------- keys ----------

export const workforceKeys = {
  all: ["workforce"] as const,
  settings: ["workforce", "settings"] as const,
  unitKinds: ["workforce", "unit-kinds"] as const,
  units: ["workforce", "units"] as const,
  unitOverview: (unitId: string) => ["workforce", "unit-overview", unitId] as const,
  employees: (q: EmployeesQueryInput = {}) =>
    ["workforce", "employees", q.q ?? "", q.unitId ?? "", q.deep ?? "", q.page ?? 1, q.ended ?? "", q.sort ?? "", q.dir ?? ""] as const,
  employee: (employeeId: string) => ["workforce", "employee", employeeId] as const,
  salaryScales: (year: number) => ["workforce", "salary-scales", year] as const,
};

// ---------- fetch functions ----------

export const workforceApi = {
  settings: () => apiV3.get<WorkforceSettings>("/workforce/settings"),
  saveSettings: (body: WorkforceSettingsInput) => apiV3.put<WorkforceSettings>("/workforce/settings", body),
  unitKinds: () => apiV3.get<OrgUnitKind[]>("/org-unit-kinds"),
  saveUnitKinds: (body: OrgUnitKindsInput) => apiV3.put<OrgUnitKind[]>("/org-unit-kinds", body),
  units: () => apiV3.get<OrgUnit[]>("/org-units"),
  createUnit: (body: OrgUnitInput) => apiV3.post<OrgUnit>("/org-units", body),
  updateUnit: (unitId: string, body: OrgUnitPatchInput) => apiV3.patch<OrgUnit>(`/org-units/${unitId}`, body),
  /** Archives; 409 while it has people or active sub-units. */
  archiveUnit: (unitId: string) => apiV3.delete(`/org-units/${unitId}`),
  unitOverview: (unitId: string) => apiV3.get<UnitOverview>(`/org-units/${unitId}/overview`),
  employees: (q: EmployeesQueryInput = {}) =>
    apiV3.get<EmployeePage>("/employees", { q: q.q, unitId: q.unitId, deep: q.deep, page: q.page, ended: q.ended, sort: q.sort, dir: q.dir }),
  createEmployee: (body: EmployeeInput) => apiV3.post<Employee>("/employees", body),
  employee: (employeeId: string) => apiV3.get<Employee>(`/employees/${employeeId}`),
  updateEmployee: (employeeId: string, body: EmployeePatchInput) => apiV3.patch<Employee>(`/employees/${employeeId}`, body),
  salaryScales: (year: number) => apiV3.get<SalaryScale[]>("/salary-scales", { year }),
  saveSalaryScales: (year: number, body: SalaryScalesInput) => apiV3.put<SalaryScale[]>(`/salary-scales?year=${year}`, body),
};

// ---------- hooks ----------

/** The settings (also the collab words for the header navigation). `enabled`: false off the /org pages. */
export function useWorkforceSettings(enabled = true) {
  return useQuery({ queryKey: workforceKeys.settings, queryFn: workforceApi.settings, staleTime: 60_000, enabled });
}

/** The words and number locale the section's frame shows before and while the settings load. */
export type OrgWords = { collabLabel: string; collabLabelPlural: string; locale: OrgLocale };

const WORDS_KEY = "convenor.org-words";

/** The last known words of this browser tab (sessionStorage), or null. Storage may be blocked: never throws. */
export function readCachedOrgWords(): OrgWords | null {
  try {
    const raw = window.sessionStorage.getItem(WORDS_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<OrgWords>;
    if (typeof v.collabLabel !== "string" || typeof v.collabLabelPlural !== "string") return null;
    return { collabLabel: v.collabLabel, collabLabelPlural: v.collabLabelPlural, locale: v.locale === "nl-NL" ? "nl-NL" : "en-GB" };
  } catch {
    return null;
  }
}

function writeCachedOrgWords(w: OrgWords): void {
  try {
    window.sessionStorage.setItem(WORDS_KEY, JSON.stringify(w));
  } catch {
    // Private mode or blocked storage: the next first paint waits for the settings instead.
  }
}

/**
 * The organisation's words (collabLabel, collabLabelPlural) and number locale
 * for the section's frame: the settings once loaded, before that the words
 * last seen in this tab. `ready` is false while neither is known, so the
 * header can hold its words back instead of flashing the defaults.
 */
export function useOrgWords(enabled = true): OrgWords & { ready: boolean } {
  const settings = useWorkforceSettings(enabled);
  const data = settings.data;
  const label = data?.collabLabel;
  const plural = data?.collabLabelPlural;
  const locale: OrgLocale | undefined = data ? (data.locale ?? "en-GB") : undefined;
  useEffect(() => {
    if (label !== undefined && plural !== undefined && locale) writeCachedOrgWords({ collabLabel: label, collabLabelPlural: plural, locale });
  }, [label, plural, locale]);
  if (label !== undefined && plural !== undefined && locale) return { collabLabel: label, collabLabelPlural: plural, locale, ready: true };
  const cached = readCachedOrgWords();
  if (cached) return { ...cached, ready: true };
  // A failed load falls back to the defaults rather than holding the words back for ever.
  return { collabLabel: "Collaboration", collabLabelPlural: "Collaborations", locale: "en-GB", ready: settings.isError || !enabled };
}

export function useSaveWorkforceSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: workforceApi.saveSettings,
    onSuccess: (data) => qc.setQueryData(workforceKeys.settings, data),
  });
}

export function useOrgUnitKinds() {
  return useQuery({ queryKey: workforceKeys.unitKinds, queryFn: workforceApi.unitKinds });
}

export function useSaveOrgUnitKinds() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: workforceApi.saveUnitKinds,
    onSuccess: (data) => {
      qc.setQueryData(workforceKeys.unitKinds, data);
      void qc.invalidateQueries({ queryKey: workforceKeys.units });
    },
  });
}

export function useOrgUnits() {
  return useQuery({ queryKey: workforceKeys.units, queryFn: workforceApi.units });
}

/** After any change to the tree: units, overviews, employees (their unit names). */
function useInvalidateWorkforce() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: workforceKeys.all });
}

export function useCreateOrgUnit() {
  const invalidate = useInvalidateWorkforce();
  return useMutation({ mutationFn: workforceApi.createUnit, onSuccess: () => invalidate() });
}

export function useUpdateOrgUnit() {
  const invalidate = useInvalidateWorkforce();
  return useMutation({ mutationFn: ({ unitId, body }: { unitId: string; body: OrgUnitPatchInput }) => workforceApi.updateUnit(unitId, body), onSuccess: () => invalidate() });
}

export function useArchiveOrgUnit() {
  const invalidate = useInvalidateWorkforce();
  return useMutation({ mutationFn: workforceApi.archiveUnit, onSuccess: () => invalidate() });
}

export function useUnitOverview(unitId: string | undefined) {
  return useQuery({ queryKey: workforceKeys.unitOverview(unitId ?? ""), queryFn: () => workforceApi.unitOverview(unitId!), enabled: !!unitId });
}

/** The register, 50 a page; keeps the previous page on screen while the next loads. `enabled`: false to wait (e.g. an empty search). */
export function useEmployees(q: EmployeesQueryInput = {}, enabled = true) {
  return useQuery({ queryKey: workforceKeys.employees(q), queryFn: () => workforceApi.employees(q), placeholderData: keepPreviousData, enabled });
}

export function useCreateEmployee() {
  const invalidate = useInvalidateWorkforce();
  return useMutation({ mutationFn: workforceApi.createEmployee, onSuccess: () => invalidate() });
}

export function useEmployee(employeeId: string | undefined) {
  return useQuery({ queryKey: workforceKeys.employee(employeeId ?? ""), queryFn: () => workforceApi.employee(employeeId!), enabled: !!employeeId });
}

export function useUpdateEmployee() {
  const invalidate = useInvalidateWorkforce();
  return useMutation({
    mutationFn: ({ employeeId, body }: { employeeId: string; body: EmployeePatchInput }) => workforceApi.updateEmployee(employeeId, body),
    onSuccess: () => invalidate(),
  });
}

export function useSalaryScales(year: number, enabled = true) {
  return useQuery({ queryKey: workforceKeys.salaryScales(year), queryFn: () => workforceApi.salaryScales(year), enabled });
}

export function useSaveSalaryScales(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SalaryScalesInput) => workforceApi.saveSalaryScales(year, body),
    onSuccess: (data) => qc.setQueryData(workforceKeys.salaryScales(year), data),
  });
}
